import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AddTaskIcon from '@mui/icons-material/AddTask';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import BankerDocumentsView from './BankerDocumentsView';
import { formatCurrency } from '../../../helper/currencyFormatter.helper';
import { ROLES, dealsStatus } from '../../../constants/general.constant';
import { Deal, Customer } from '../../../validation/customer.validation';
import { Banker } from './assignedBankerDialog';
import { getStatusColor } from './dealsStatusColor';

type Props = {
  deals: Deal[];
  loggedInUser: Customer | null;
  expandedRows: Set<string>;
  onToggleExpand: (dealId: string) => void;
  onAssignClick: (deal: Deal) => void;
  onAddTaskClick?: (deal: Deal) => void;
  onEditClick: (deal: Deal) => void;
  onDeleteClick: (dealId?: string) => void;
  onViewTermSheet: (deal: Deal, bankerId: string) => void;
  onClosingClick: (deal: Deal) => void;
  actionsDisabled?: boolean;
};

const renderAssignedBankers = (
  assignedBankers: any[],
  onViewTermSheet: (bankerId: string) => void
) => {
  if (!assignedBankers || assignedBankers.length === 0) {
    return (
      <Chip
        label="Unassigned"
        size="small"
        variant="outlined"
        color="default"
        sx={{ fontWeight: 700 }}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {assignedBankers.map((pivot) => {
        const banker = pivot.bank as Banker | undefined | null;

        if (!banker) {
          return (
            <Chip
              key={pivot.bankId || 'unknown'}
              label="?"
              size="small"
              color="error"
              variant="filled"
              sx={{ fontWeight: 900 }}
            />
          );
        }

        const initials =
          (banker as any).firstName ||
          banker.name ||
          (banker as any).first ||
          'B';

        let color: 'primary' | 'success' | 'error' = 'primary';
        if (pivot.interestStatus === dealsStatus.Approved) color = 'success';
        else if (pivot.interestStatus === dealsStatus.Rejected) color = 'error';

        return (
          <Box key={(banker as any).id || pivot.bankId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip
              title={
                `${(banker as any).firstName || ''} ${(banker as any).middleName || ''} ${(banker as any).lastName || ''}`
                  .trim() || banker.name || 'Unknown Banker'
              }
            >
              <Chip
                label={initials}
                size="small"
                color={color}
                variant="filled"
                sx={{ fontWeight: 900 }}
              />
            </Tooltip>

            {pivot.interestStatus === dealsStatus.Approved && (
              <Tooltip title="View Term Sheet">
                <IconButton
                  size="small"
                  onClick={() => onViewTermSheet(banker.id)}
                  color="primary"
                  sx={{
                    border: '1px solid rgba(0,0,0,0.12)',
                    backgroundColor: '#fff',
                    '&:hover': { backgroundColor: '#f3f4f6' },
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const getDealCustomerName = (deal: Deal) => {
  const customer: any = (deal as any).customer;
  if (!customer) return '-';
  return (
    `${customer.firstName || ''} ${customer.middleName || ''} ${customer.lastName || ''}`.trim() ||
    customer.name ||
    '-'
  );
};

const getDealCreatedBy = (deal: Deal) => {
  const customer: any = (deal as any).customer;
  if (!customer) return '-';

  if (customer.createdByUser) {
    const u: any = customer.createdByUser;
    return `${u.firstName || ''} ${u.middleName || ''} ${u.lastName || ''}`.trim() || u.name || '-';
  }

  if (customer.createdByBusiness) {
    const b: any = customer.createdByBusiness;
    return `${b.firstName || ''} ${b.middleName || ''} ${b.lastName || ''}`.trim() || b.name || '-';
  }

  return '-';
};

const formatClosingDate = (date?: string | null) => {
  if (!date) return 'Add Date';
  const [year, month, day] = date.slice(0, 10).split('-');
  return year && month && day ? `${month}/${day}/${year}` : date;
};

const DealsMobileCardView: React.FC<Props> = ({
  deals,
  loggedInUser,
  expandedRows,
  onToggleExpand,
  onAssignClick,
  onAddTaskClick = () => undefined,
  onEditClick,
  onDeleteClick,
  onViewTermSheet,
  onClosingClick,
  actionsDisabled = false,
}) => {
  const canAssign = !!loggedInUser && loggedInUser.role !== ROLES.CUSTOMER;

  if (deals.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', color: '#6b7280' }}>
        <Typography sx={{ fontWeight: 800 }}>No deals found</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {deals.map((d) => {
        const isExpanded = expandedRows.has(d.id!);
        const hasBankers = !!d?.assignedBanks && d.assignedBanks.length > 0;

        return (
          <Card
            key={d.id}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: "#e5e7eb",
              boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Top row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: 16,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {d.loanName}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      mt: 0.5,
                      fontWeight: 700,
                    }}
                  >
                    {d.dealType
                      ? d.dealType.charAt(0).toUpperCase() + d.dealType.slice(1)
                      : "-"}
                  </Typography>
                </Box>

                {hasBankers ? (
                  <IconButton
                    size="small"
                    onClick={() => onToggleExpand(d.id!)}
                    sx={{
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#f3f4f6" },
                    }}
                    aria-label="toggle-details"
                  >
                    {isExpanded ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowRightIcon />
                    )}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 40 }} />
                )}
              </Box>

              {/* Summary */}
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "grid", gap: 1, textAlign: "left" }}>
                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Customer:{" "}
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          wordBreak: "break-word",
                          color: "#5a5353",
                        }}
                      >
                        {getDealCustomerName(d)}
                      </span>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Loan Size:{" "}
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          wordBreak: "break-word",
                          color: "#5a5353",
                        }}
                      >
                        {formatCurrency(d.loanSize)}
                      </span>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Honor:{" "}
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          wordBreak: "break-word",
                          color: "#5a5353",
                        }}
                      >
                        {d?.customer && (d as any)?.customer?.createdByUser
                          ? `${(d as any)?.customer?.createdByUser?.firstName || ""} ${(d as any)?.customer?.createdByUser?.middleName || ""} ${(d as any)?.customer?.createdByUser?.lastName || ""}`.trim() ||
                            (d as any)?.customer?.createdByUser?.name
                          : (d as any)?.customer?.createdByBusiness
                            ? `${(d as any)?.customer?.createdByBusiness?.firstName || ""} ${(d as any)?.customer?.createdByBusiness?.middleName || ""} ${(d as any)?.customer?.createdByBusiness?.lastName || ""}`.trim() ||
                              (d as any)?.customer?.createdByBusiness?.name
                            : "-"}
                      </span>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Status:{" "}
                      <span>
                        <Chip
                          label={d.status || "-"}
                          color={getStatusColor(d.status) as any}
                          variant="filled"
                          size="small"
                          onClick={actionsDisabled ? undefined : () => onClosingClick(d)}
                          sx={{ fontWeight: 700, verticalAlign: "middle", cursor: actionsDisabled ? "default" : "pointer" }}
                        />
                      </span>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Closing:{" "}
                      <span>
                        <Chip
                          label={formatClosingDate(d.closingDate)}
                          color={d.closingDate ? "success" : "default"}
                          variant={d.closingDate ? "filled" : "outlined"}
                          size="small"
                          onClick={actionsDisabled ? undefined : () => onClosingClick(d)}
                          sx={{ fontWeight: 700, verticalAlign: "middle", cursor: actionsDisabled ? "default" : "pointer" }}
                        />
                      </span>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Max LTV:{" "}
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          wordBreak: "break-word",
                          color: "#5a5353",
                        }}
                      >
                        {d.maxLTV ?? "-"}%
                      </span>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: "0.5px solid #d7d7d7",
                      borderRadius: 2,
                      p: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: 0.2,
                      }}
                    >
                      Min DSCR:{" "}
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          wordBreak: "break-word",
                          color: "#5a5353",
                        }}
                      >
                        {d.minDSCRatio ?? "-"}
                      </span>
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Assigned bankers */}
              <Box sx={{ mt: 1.25 }}>
                <Typography
                  sx={{ fontSize: 11, color: "#6b7280", fontWeight: 900 }}
                >
                  Assigned Bankers
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  {renderAssignedBankers(d.assignedBanks as any[], (bankerId) =>
                    onViewTermSheet(d, bankerId),
                  )}
                </Box>
              </Box>

              {/* Actions */}
              <Box
                sx={{
                  mt: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "wrap",
                  }}
                >
                  {canAssign && (
                    <Tooltip title={actionsDisabled ? "Actions disabled" : "Assign Bankers"}>
                      <span>
                      <IconButton
                        size="small"
                        onClick={() => onAssignClick(d)}
                        color="primary"
                        disabled={actionsDisabled}
                        sx={{
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 2,
                          backgroundColor: "#fff",
                          "&:hover": { backgroundColor: "#f3f4f6" },
                        }}
                      >
                        <GroupAddIcon fontSize="small" />
                      </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  <Tooltip title={actionsDisabled ? "Actions disabled" : "Edit"}>
                    <span>
                    <IconButton
                      size="small"
                      onClick={() => onEditClick(d)}
                      color="secondary"
                      disabled={actionsDisabled}
                      sx={{
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={actionsDisabled ? "Actions disabled" : "Add Task"}>
                    <span>
                    <IconButton
                      size="small"
                      onClick={() => onAddTaskClick(d)}
                      color="info"
                      disabled={actionsDisabled}
                      sx={{
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      <AddTaskIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={actionsDisabled ? "Actions disabled" : "Delete"}>
                    <span>
                    <IconButton
                      size="small"
                      onClick={() => onDeleteClick(d.id)}
                      color="error"
                      disabled={actionsDisabled}
                      sx={{
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Typography
                  sx={{ fontSize: 11, color: "#6b7280", fontWeight: 900 }}
                >
                  Created by: {getDealCreatedBy(d)}
                </Typography>
              </Box>

              {/* Expanded details */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 1.25 }}>
                  {hasBankers &&
                    d.assignedBanks.map((pivot: any) => {
                      const banker = pivot.bank;
                      if (!banker) return null;

                      return (
                        <Box key={banker.id} sx={{ mb: 1 }}>
                          <BankerDocumentsView
                            dealId={d.id!}
                            bankerId={banker.id}
                            bankerName={
                              `${banker.firstName || ""} ${banker.middleName || ""} ${banker.lastName || ""}`.trim() ||
                              banker.name
                            }
                            onViewTermSheet={(bankerId) =>
                              onViewTermSheet(d, bankerId)
                            }
                          />
                        </Box>
                      );
                    })}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default DealsMobileCardView;
